import React, { createContext, useContext, useState, useEffect } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../services/maps';
import { debug } from '../utils/debug';

const GoogleMapsContext = createContext(null);

// Create loader options only if we have all required values
const getLoaderOptions = () => {
  debug.log('Creating Google Maps loader options with config:', {
    apiKey: GOOGLE_MAPS_CONFIG.apiKey ? 'Set' : 'Not Set',
    mapIds: GOOGLE_MAPS_CONFIG.mapIds,
    version: GOOGLE_MAPS_CONFIG.version,
    libraries: GOOGLE_MAPS_CONFIG.libraries
  });

  if (!GOOGLE_MAPS_CONFIG.apiKey || !GOOGLE_MAPS_CONFIG.mapIds?.length) {
    debug.error('Missing required Google Maps configuration:', {
      apiKey: GOOGLE_MAPS_CONFIG.apiKey ? 'Set' : 'Not Set',
      mapIds: GOOGLE_MAPS_CONFIG.mapIds
    });
    return null;
  }

  const options = {
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_CONFIG.apiKey,
    version: GOOGLE_MAPS_CONFIG.version,
    mapIds: GOOGLE_MAPS_CONFIG.mapIds,
    libraries: GOOGLE_MAPS_CONFIG.libraries
  };

  debug.log('Created Google Maps loader options:', {
    ...options,
    googleMapsApiKey: options.googleMapsApiKey ? 'Set' : 'Not Set'
  });

  return options;
};

export function GoogleMapsProvider({ children }) {
  debug.log('Initializing GoogleMapsProvider');
  
  const loaderOptions = getLoaderOptions();
  const { isLoaded, loadError } = useJsApiLoader(loaderOptions || {});

  debug.log('Google Maps loader state:', {
    isLoaded,
    hasError: !!loadError,
    errorMessage: loadError?.message,
    hasOptions: !!loaderOptions
  });

  const value = {
    isLoaded: loaderOptions ? isLoaded : false,
    loadError: loaderOptions ? loadError : new Error('Missing Google Maps configuration'),
    loaderOptions
  };

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
}

export function useGoogleMaps() {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }
  return context;
} 